import React from 'react';
import { Button } from './ui/button';
import { ArrowRight, Calendar, Download, CheckCircle, Award, Users, Globe } from 'lucide-react';
import AnimatedCounter from './AnimatedCounter';
import { siteContent, companyInfo } from '../data/mockData';
import { useScrollAnimation, useStaggeredAnimation } from '../hooks/useScrollAnimation';

const FinalCTA = ({ onNavigate }) => {
  const content = siteContent.en?.finalCTA || siteContent.en.finalCTA;
  const [headerRef, headerVisible] = useScrollAnimation();
  const [statsRef, visibleStats] = useStaggeredAnimation(4, 150);
  const [ctaRef, ctaVisible] = useScrollAnimation(300);
  const [contactRef, contactVisible] = useScrollAnimation(500);

  const scrollToContact = () => {
    if (onNavigate) {
      onNavigate('contact');
    }
  };

  const handleDownloadCapabilities = () => {
    // Mock download functionality
    alert('Capabilities deck download would start here');
  };

  const openCalendly = () => {
    window.open(companyInfo.calendlyUrl, '_blank');
  };

  return (
    <section id="final-cta" className="py-20 lg:py-32 bg-gradient-to-br from-[#011431] via-[#c41927] to-[#011431] text-white relative overflow-hidden">
      {/* Background Pattern - Removed circular elements */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-40 right-20 w-24 h-24 border border-white rounded-sm transform rotate-45"></div>
      </div>

      <div className="container mx-auto px-4 lg:px-6 relative">
        <div className="max-w-4xl mx-auto text-center">
          {/* Main CTA Content with Large Q Logo */}
          <div ref={headerRef} className={`flex items-center justify-center mb-6 animate-fade-in-up ${headerVisible ? 'visible' : ''}`}>
            <img 
              src="https://customer-assets.emergentagent.com/job_meddevice-pro/artifacts/s4i3oame_CheckLogo_No-background.png" 
              alt="MedQuore Quality" 
              className="w-16 h-16 mr-6 flex-shrink-0"
            />
            <h2 className="text-3xl lg:text-5xl font-bold leading-tight">
              {content.title}
            </h2>
          </div>
          
          <div className={`animate-fade-in-up ${headerVisible ? 'visible' : ''}`} style={{transitionDelay: '0.2s'}}>
            <p className="text-xl lg:text-2xl mb-4 opacity-90 max-w-3xl mx-auto leading-relaxed">
              {content.subtitle}
            </p>

            <p className="text-lg mb-12 opacity-80 max-w-2xl mx-auto">
              {content.description}
            </p>
          </div>

          {/* Trust Indicators - Replace Q logo with normal icons */}
          <div className="grid md:grid-cols-2 gap-6 mb-12 max-w-2xl mx-auto">
            {content.trustIndicators.map((indicator, index) => (
              <div key={index} className="flex items-center justify-center space-x-3 bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg px-6 py-4">
                <CheckCircle className="w-6 h-6 text-white flex-shrink-0" />
                <span className="text-white font-medium text-center">{indicator}</span>
              </div>
            ))}
          </div>

          {/* CTA Buttons */}
          <div ref={ctaRef} className={`flex flex-col lg:flex-row gap-6 justify-center items-center mb-16 animate-slide-in-bottom ${ctaVisible ? 'visible' : ''}`}>
            <Button
              onClick={scrollToContact}
              className="bg-white text-[#011431] hover:bg-gray-100 font-semibold px-8 py-4 text-lg group transition-all duration-300 transform hover:scale-105 shadow-xl"
            >
              <Calendar className="w-5 h-5 mr-2" />
              {content.primaryCTA}
              <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
            </Button>
            
            <Button
              onClick={handleDownloadCapabilities}
              variant="outline"
              className="border-white text-white hover:bg-white hover:text-[#011431] font-semibold px-8 py-4 text-lg transition-all duration-300 bg-transparent"
            >
              <Download className="w-5 h-5 mr-2" />
              {content.secondaryCTA}
            </Button>
          </div>

          {/* Stats Grid with World Map Background and Animated Counters */}
          <div 
            ref={statsRef}
            className="relative grid md:grid-cols-4 gap-8 max-w-4xl mx-auto border-t border-white/20 pt-12 pb-8"
            style={{
              backgroundImage: `url('https://customer-assets.emergentagent.com/job_meddevice-pro/artifacts/y6m6l5vl_Picture1.png')`,
              backgroundSize: 'contain',
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'center'
            }}
          >
            {/* Overlay for better text readability */}
            <div className="absolute inset-0 bg-black/20 rounded-lg"></div>
            
            {siteContent.en?.stats?.map((stat, index) => {
              const icons = [Award, Users, Globe, CheckCircle];
              const IconComponent = icons[index];
              return (
                <div key={index} className={`text-center group relative z-10 animate-scale-up ${visibleStats.includes(index) ? 'visible' : ''}`}>
                  <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-lg flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                    <IconComponent className="w-8 h-8 text-white" />
                  </div>
                  <div className="text-3xl font-bold mb-2 text-white drop-shadow-lg">
                    <AnimatedCounter 
                      endValue={stat.value} 
                      duration={2500}
                    />
                  </div>
                  <div className="text-sm opacity-90 text-white drop-shadow-md font-medium">{stat.label}</div>
                </div>
              );
            }) || siteContent.en.stats.map((stat, index) => {
              const icons = [Award, Users, Globe, CheckCircle];
              const IconComponent = icons[index];
              return (
                <div key={index} className="text-center group relative z-10">
                  <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-lg flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                    <IconComponent className="w-8 h-8 text-white" />
                  </div>
                  <div className="text-3xl font-bold mb-2 text-white drop-shadow-lg">
                    <AnimatedCounter 
                      endValue={stat.value} 
                      duration={2500}
                    />
                  </div>
                  <div className="text-sm opacity-90 text-white drop-shadow-md font-medium">{stat.label}</div>
                </div>
              );
            })}
          </div>

          {/* Contact Information - Simple icon instead of Q logo */}
          <div className="mt-16 pt-8 border-t border-white/20 text-center">
            <div ref={contactRef} className={`flex items-center justify-center mb-4 animate-fade-in-up ${contactVisible ? 'visible' : ''}`}>
              <ArrowRight className="w-6 h-6 text-white mr-3" />
              <p className="text-sm opacity-80">
                Questions? Contact us directly at{' '}
                <a 
                  href={`mailto:${companyInfo.email}`}
                  className="text-white underline hover:text-gray-200 transition-colors duration-200 cursor-pointer"
                  title={`Send email to ${companyInfo.email}`}
                >
                  {companyInfo.email}
                </a>
              </p>
            </div>
            <div className={`animate-fade-in-up ${contactVisible ? 'visible' : ''}`} style={{transitionDelay: '0.2s'}}>
              <p className="text-xs opacity-60">
                Serving clients across {companyInfo.regions.join(', ')}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FinalCTA;